// src/components/BetaGate.jsx
// Pre-login access screen — two panels:
//   1. Enter a beta code → proceed to /login
//   2. Join the waitlist → name + email + optional reason → Supabase insert

import { useState } from "react";
import { supabase } from "../lib/supabaseClient";

const BG      = "#0a0a0a";
const BG2     = "#111111";
const BG3     = "#0d1117";
const BORDER  = "#1c2333";
const GREEN   = "#3ddc84";
const RED     = "#ff6b35";
const TEXT    = "#c9d1d9";
const MUTED   = "#8b949e";
const MONO    = "var(--font-mono, 'JetBrains Mono', monospace)";
const DISPLAY = "var(--font-display, 'Syne', sans-serif)";
const BODY    = "var(--font-body, 'IBM Plex Sans', sans-serif)";

// Validate a code against the REACT_APP_BETA_CODES env var (comma-separated list)
function isValidCode(raw) {
  const codes = (process.env.REACT_APP_BETA_CODES || "")
    .split(",")
    .map(c => c.trim().toUpperCase())
    .filter(Boolean);
  return codes.includes(raw.trim().toUpperCase());
}

function genReferralCode() {
  return "NV-" + Math.random().toString(36).slice(2, 8).toUpperCase();
}

export default function BetaGate({ onCodeAccepted, onSignIn }) {
  const [panel, setPanel] = useState("code"); // "code" | "waitlist"

  // ── code panel state ──────────────────────────────────────────────────────
  const [code,      setCode]      = useState("");
  const [codeState, setCodeState] = useState("idle"); // "idle" | "error"

  function handleCodeChange(e) {
    const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "");
    setCode(raw);
    setCodeState("idle");
  }

  function handleCodeSubmit(e) {
    e.preventDefault();
    if (!code.trim()) return;
    if (isValidCode(code)) {
      onCodeAccepted();
    } else {
      setCodeState("error");
    }
  }

  // ── waitlist panel state ──────────────────────────────────────────────────
  const [wlName,   setWlName]   = useState("");
  const [wlEmail,  setWlEmail]  = useState("");
  const [wlReason, setWlReason] = useState("");
  const [wlState,  setWlState]  = useState("idle"); // "idle" | "loading" | "success" | "duplicate" | "error"
  const [wlErr,    setWlErr]    = useState("");

  async function handleWaitlistSubmit(e) {
    e.preventDefault();
    if (!wlName.trim() || !wlEmail.trim() || wlState === "loading") return;
    setWlState("loading");
    setWlErr("");

    if (!supabase) {
      setWlState("success");
      return;
    }

    const { error } = await supabase.from("waitlist").insert({
      name:            wlName.trim(),
      email:           wlEmail.trim().toLowerCase(),
      interest_reason: wlReason.trim() || null,
      referral_code:   genReferralCode(),
    });

    if (error) {
      if (error.code === "23505") {
        setWlState("duplicate");
      } else {
        setWlState("error");
        setWlErr("Something went wrong. Please try again.");
      }
      return;
    }

    setWlState("success");
  }

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      background: BG,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
      zIndex: 9000,
    }}>

      {/* Wordmark */}
      <div style={{
        fontFamily: DISPLAY,
        fontSize: 22,
        fontWeight: 800,
        color: GREEN,
        letterSpacing: ".04em",
        marginBottom: 40,
      }}>
        NUVPORT OS
      </div>

      {/* Card */}
      <div style={{
        background: BG2,
        border: `1px solid ${BORDER}`,
        borderTop: `2px solid ${GREEN}`,
        borderRadius: 12,
        width: "100%",
        maxWidth: 440,
        overflow: "hidden",
      }}>

        {/* Tab switcher */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          borderBottom: `1px solid ${BORDER}`,
        }}>
          {[
            { key: "code",     label: "I have a code" },
            { key: "waitlist", label: "Join waitlist"  },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setPanel(key)}
              style={{
                background: panel === key ? BG3 : "transparent",
                border: "none",
                borderBottom: panel === key ? `2px solid ${GREEN}` : "2px solid transparent",
                padding: "14px 0",
                fontFamily: MONO,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: ".1em",
                textTransform: "uppercase",
                color: panel === key ? GREEN : MUTED,
                cursor: "pointer",
                transition: "all .15s",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Panel body */}
        <div style={{ padding: "32px 36px 36px" }}>

          {/* ── CODE PANEL ──────────────────────────────────────────────── */}
          {panel === "code" && (
            <>
              <div style={{ fontFamily: DISPLAY, fontSize: 20, fontWeight: 700, color: TEXT, marginBottom: 8 }}>
                Enter your beta code
              </div>
              <div style={{ fontFamily: BODY, fontSize: 13, color: MUTED, lineHeight: 1.6, marginBottom: 28 }}>
                Nuvport OS is currently in private beta.<br />
                Got a code? Enter it below to get access.
              </div>

              <form onSubmit={handleCodeSubmit}>
                <input
                  type="text"
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="NVPT-XXXXXXX"
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  style={{
                    width: "100%",
                    background: BG3,
                    border: `1px solid ${codeState === "error" ? RED : BORDER}`,
                    borderRadius: 8,
                    padding: "12px 16px",
                    fontFamily: MONO,
                    fontSize: 16,
                    fontWeight: 700,
                    color: TEXT,
                    letterSpacing: ".12em",
                    outline: "none",
                    boxSizing: "border-box",
                    transition: "border-color .15s",
                  }}
                  onFocus={e => { if (codeState !== "error") e.currentTarget.style.borderColor = GREEN; }}
                  onBlur={e  => { if (codeState !== "error") e.currentTarget.style.borderColor = BORDER; }}
                />

                {codeState === "error" && (
                  <div style={{ fontFamily: MONO, fontSize: 11, color: RED, marginTop: 8, letterSpacing: ".04em" }}>
                    Invalid code. Request access below or contact us.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!code.trim()}
                  style={{
                    width: "100%",
                    marginTop: 20,
                    background: code.trim() ? GREEN : BORDER,
                    border: "none",
                    borderRadius: 8,
                    padding: "13px 0",
                    fontFamily: MONO,
                    fontSize: 12,
                    fontWeight: 700,
                    color: code.trim() ? BG : MUTED,
                    letterSpacing: ".08em",
                    cursor: code.trim() ? "pointer" : "default",
                    transition: "all .15s",
                  }}
                >
                  CONTINUE →
                </button>
              </form>
            </>
          )}

          {/* ── WAITLIST PANEL ───────────────────────────────────────────── */}
          {panel === "waitlist" && (
            <>
              {wlState === "success" && (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ fontFamily: MONO, fontSize: 22, color: GREEN, marginBottom: 12 }}>✓</div>
                  <div style={{ fontFamily: DISPLAY, fontSize: 20, fontWeight: 700, color: TEXT, marginBottom: 8 }}>
                    You're on the list
                  </div>
                  <div style={{ fontFamily: BODY, fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
                    We'll reach out when your access is ready.
                  </div>
                </div>
              )}

              {wlState === "duplicate" && (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ fontFamily: DISPLAY, fontSize: 20, fontWeight: 700, color: TEXT, marginBottom: 8 }}>
                    Already registered
                  </div>
                  <div style={{ fontFamily: BODY, fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
                    That email is already on the waitlist. We'll be in touch soon.
                  </div>
                </div>
              )}

              {(wlState === "idle" || wlState === "loading" || wlState === "error") && (
                <>
                  <div style={{ fontFamily: DISPLAY, fontSize: 20, fontWeight: 700, color: TEXT, marginBottom: 8 }}>
                    Join the waitlist
                  </div>
                  <div style={{ fontFamily: BODY, fontSize: 13, color: MUTED, lineHeight: 1.6, marginBottom: 28 }}>
                    We'll let you know as soon as access opens up.
                  </div>

                  <form onSubmit={handleWaitlistSubmit}>
                    {/* Name */}
                    <input
                      type="text"
                      value={wlName}
                      onChange={e => setWlName(e.target.value)}
                      placeholder="Your name"
                      required
                      style={inputStyle}
                      onFocus={e => { e.currentTarget.style.borderColor = GREEN; }}
                      onBlur={e  => { e.currentTarget.style.borderColor = BORDER; }}
                    />

                    {/* Email */}
                    <input
                      type="email"
                      value={wlEmail}
                      onChange={e => setWlEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      style={{ ...inputStyle, marginTop: 12 }}
                      onFocus={e => { e.currentTarget.style.borderColor = GREEN; }}
                      onBlur={e  => { e.currentTarget.style.borderColor = BORDER; }}
                    />

                    {/* Optional reason */}
                    <textarea
                      value={wlReason}
                      onChange={e => setWlReason(e.target.value)}
                      placeholder="Why do you want access? (optional)"
                      rows={3}
                      style={{
                        ...inputStyle,
                        marginTop: 12,
                        resize: "vertical",
                        minHeight: 72,
                        fontFamily: BODY,
                        fontSize: 13,
                        lineHeight: 1.5,
                      }}
                      onFocus={e => { e.currentTarget.style.borderColor = GREEN; }}
                      onBlur={e  => { e.currentTarget.style.borderColor = BORDER; }}
                    />

                    {wlErr && (
                      <div style={{ fontFamily: MONO, fontSize: 11, color: RED, marginTop: 8, letterSpacing: ".04em" }}>
                        {wlErr}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={!wlName.trim() || !wlEmail.trim() || wlState === "loading"}
                      style={{
                        width: "100%",
                        marginTop: 20,
                        background: (wlName.trim() && wlEmail.trim() && wlState !== "loading") ? GREEN : BORDER,
                        border: "none",
                        borderRadius: 8,
                        padding: "13px 0",
                        fontFamily: MONO,
                        fontSize: 12,
                        fontWeight: 700,
                        color: (wlName.trim() && wlEmail.trim() && wlState !== "loading") ? BG : MUTED,
                        letterSpacing: ".08em",
                        cursor: (wlName.trim() && wlEmail.trim() && wlState !== "loading") ? "pointer" : "default",
                        transition: "all .15s",
                      }}
                    >
                      {wlState === "loading" ? "JOINING..." : "JOIN WAITLIST →"}
                    </button>
                  </form>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Footer — sign in link for returning users */}
      <div style={{
        marginTop: 24,
        fontFamily: MONO,
        fontSize: 11,
        color: MUTED,
        letterSpacing: ".06em",
        textAlign: "center",
      }}>
        Already have an account?{" "}
        <span
          onClick={onSignIn}
          style={{ color: GREEN, cursor: "pointer", textDecoration: "underline" }}
        >
          Sign in
        </span>
      </div>

    </div>
  );
}

const inputStyle = {
  width: "100%",
  background: "#0d1117",
  border: `1px solid ${BORDER}`,
  borderRadius: 8,
  padding: "11px 14px",
  fontFamily: BODY,
  fontSize: 14,
  color: TEXT,
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color .15s",
  display: "block",
};
